/**
 * Проверка выполнимости варианта ПП — этап 4 планирования сезона (UC-4).
 *
 * Макет (утверждён founder):
 * `docs/superpowers/prototypes/season-planning-2027/05-feasibility.html`.
 *
 * Что экран обещает пользователю:
 *   • ответ на вопрос «успеем ли в агросроки тем, что есть» — по каждому окну
 *     сезона: сколько сцепок нужно и (если парк заведён) сколько их есть;
 *   • узкие места с тремя исходами (Р17): принять риск с комментарием, сменить
 *     техкарту, сменить посевной план — последние два это возврат на предыдущие
 *     этапы, а не кнопка «починить»;
 *   • честный список того, чего в расчёте НЕТ: операции без агроокна и без
 *     нормы выработки перечислены поимённо (FR-4.5), а не растворены в нулях;
 *   • этап можно пропустить (Р33) — бюджет собирается и утверждается без него,
 *     вариант просто уходит в сравнение с пометкой «не проверялась».
 *
 * Чего здесь СОЗНАТЕЛЬНО нет:
 *   • красного цвета в базовом режиме. Пока парка нет, дефицита не существует —
 *     сравнивать не с чем, и подсвечивать «плохо» значило бы выдумать факт;
 *   • автоподбора «докупите два комбайна». Решение о технике принимает человек,
 *     система показывает цифру и её основание.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import {
  needText,
  timelineBar,
  timelineScale,
  useAcceptRisk,
  useCancelRisk,
  useFeasibility,
  useMarkChecked,
  verdictTitle,
  volumeText,
  formatWindow,
  type Feasibility,
  type FeasibilityMode,
  type FeasibilityWindow,
} from '@/lib/planning/feasibility';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { usePlanVariants } from '@/lib/planning/techCards';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { VariantTabs } from './VariantTabs';

export function PlanVariantFeasibilityPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as { farm?: string; variant?: string };
  const navigate = useNavigate();
  const season = useSeasonByYear(year);
  const canRead = useCan('tech_cards', 'r');
  const canWrite = useCan('season_plan', 'u');

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897).
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant/feasibility',
        params: { year: String(year) },
        search: { farm: undefined, variant: undefined },
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant/feasibility',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  const variants = usePlanVariants(canRead ? farmId : null, season.seasonId);
  const variantId =
    search.variant ??
    variants.data?.find((v) => v.is_selected)?.id ??
    variants.data?.[0]?.id ??
    null;

  // `auto` — режим выбирает бэк (полный, если парк заведён). Переключатель
  // существует, чтобы посмотреть «а сколько нужно вообще», не разбирая парк.
  const [mode, setMode] = useState<FeasibilityMode>('auto');
  const check = useFeasibility(canRead ? variantId : null, mode);
  const markChecked = useMarkChecked();

  const setSearch = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: '/planning/seasons/$year/plan-variant/feasibility',
      params: { year: String(year) },
      search: { farm: search.farm, variant: variantId ?? undefined, ...patch },
    });
  };

  const goToSummary = () =>
    void navigate({
      to: '/planning/seasons/$year/plan-variant/summary',
      params: { year: String(year) },
      search: { variant: variantId ?? undefined },
    });

  if (!season.validYear || season.missing) {
    return (
      <Shell year={params.year}>
        <SeasonYearOutOfWindowCard year={year} />
      </Shell>
    );
  }
  if (season.isLoading) {
    return (
      <Shell year={params.year}>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }
  if (season.notOpened) {
    return (
      <Shell year={params.year}>
        <SeasonNotStartedCard year={year} onOpened={() => undefined} />
      </Shell>
    );
  }
  if (!canRead) {
    return (
      <Shell year={params.year}>
        <Alert>
          <AlertDescription>У вас нет доступа к техкартам варианта плана.</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const data = check.data;

  return (
    <Shell year={params.year} title={data ? `Выполнимость варианта «${data.scenario.name}»` : undefined}>
      <FarmScopeNotice scope={farmScope} />
      {farmId && (variants.data ?? []).length > 0 && (
        <VariantTabs
          variants={variants.data ?? []}
          currentId={variantId}
          onSelect={(id) => setSearch({ variant: id })}
        />
      )}

      {variantId && check.isLoading && <Skeleton className="h-96 w-full" />}
      {variantId && check.isError && (
        <Alert variant="destructive">
          <AlertDescription>{describeApiError(check.error)}</AlertDescription>
        </Alert>
      )}
      {!variantId && !variants.isLoading && farmId && (
        <Alert>
          <AlertDescription>
            У этого хозяйства нет вариантов плана на сезон {year} — проверять нечего.
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          {/* Р33 — проверку можно пропустить. Баннер стоит ПЕРВЫМ, до цифр:
              человек должен узнать, что этап необязателен, раньше, чем начнёт
              разбирать дефициты. */}
          <Alert>
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>
                <b className="text-foreground">Проверку можно пропустить.</b>{' '}
                {data.full_mode_available
                  ? 'Вариант уйдёт в сравнение с пометкой «выполнимость не проверялась» — это не мешает утвердить бюджет.'
                  : 'Парк техники в хозяйстве не ведётся. Вариант уйдёт в сравнение с пометкой «выполнимость не проверялась» — это не мешает утвердить бюджет.'}
              </span>
              <Button variant="outline" size="sm" onClick={goToSummary}>
                Пропустить и перейти к сравнению
              </Button>
            </AlertDescription>
          </Alert>

          {!data.gate.ok ? (
            <GateBlocked
              data={data}
              onFix={() =>
                navigate({
                  to: '/planning/seasons/$year/plan-variant',
                  params: { year: String(year) },
                  search: { variant: variantId ?? undefined },
                })
              }
            />
          ) : (
            <>
              <ModeSwitch
                data={data}
                mode={mode}
                onChange={setMode}
                onFleet={() =>
                  farmId &&
                  navigate({
                    to: '/farm/$farmId',
                    params: { farmId },
                    search: { tab: 'machinery' },
                  })
                }
              />

              <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <div className="space-y-4">
                  <Timeline windows={data.windows} />
                  <Bottlenecks
                    data={data}
                    canWrite={canWrite}
                    onChangeCard={() =>
                      navigate({
                        to: '/planning/seasons/$year/plan-variant',
                        params: { year: String(year) },
                        search: { variant: variantId ?? undefined },
                      })
                    }
                    onChangePlan={() =>
                      navigate({
                        to: '/planning/seasons/$year/sowing-plan',
                        params: { year: String(year) },
                        search: { variant: variantId ?? undefined },
                      })
                    }
                  />
                </div>

                <aside className="space-y-4">
                  <VerdictCard data={data} />
                  <ExclusionsCard data={data} />
                  <FleetCard data={data} />
                </aside>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    if (canWrite) markChecked.mutate({ scenarioId: data.scenario.id });
                    goToSummary();
                  }}
                >
                  К сравнению вариантов →
                </Button>
                {data.checked_at && (
                  <span className="self-center text-xs text-muted-foreground">
                    Проверено {new Date(data.checked_at).toLocaleString('ru-RU')}
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Shell>
  );
}

function Shell({
  year,
  title,
  children,
}: {
  year?: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <PageHeader
        title={title ?? 'Проверка выполнимости варианта'}
        description="Успеем ли в агросроки тем, что есть: по каждому окну техкарт — сколько сцепок нужно и сколько их в парке хозяйства. Этап необязательный."
        breadcrumbs={[
          { label: 'Планирование сезона', to: '/planning' },
          {
            label: `Бюджет сезона ${year ?? ''}`.trim(),
            ...(year ? { to: `/planning/seasons/${year}/budget` } : {}),
          },
          { label: 'Выполнимость' },
        ]}
      />
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </AppShell>
  );
}

/** Гейт Р16: пока не у каждой строки есть карта — считать нечего. */
function GateBlocked({ data, onFix }: { data: Feasibility; onFix: () => void }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="text-sm font-semibold text-foreground">
          {data.gate.rows_total === 0
            ? 'В варианте нет ни одной культуры — проверять нечего'
            : 'Не у каждой строки плана есть техкарта'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {data.gate.rows_total === 0
            ? 'Заведите культуры в вариант плана и назначьте им техкарты — тогда появятся агроокна, по которым считается выполнимость.'
            : `Покрыто ${data.gate.rows_covered} из ${data.gate.rows_total} строк. ${Math.round(
                data.gate.area_uncovered_ha,
              ).toLocaleString('ru-RU')} га остаются без техкарты — по ним не из чего взять ни агроокна, ни норму выработки, и «выполнимо» было бы посчитано не по всему плану.`}
        </p>
        {data.gate.uncovered.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-foreground">
            {data.gate.uncovered.map((row) => (
              <li key={`${row.crop_name}-${row.predecessor_name}`}>
                {row.crop_name ?? 'культура'}
                {row.predecessor_name ? ` по ${row.predecessor_name}` : ''} —{' '}
                {Math.round(row.area_ha).toLocaleString('ru-RU')} га, полей {row.fields_count}
              </li>
            ))}
          </ul>
        )}
        <Button size="sm" className="mt-3" onClick={onFix}>
          Назначить техкарты →
        </Button>
      </CardContent>
    </Card>
  );
}

function ModeSwitch({
  data,
  mode,
  onChange,
  onFleet,
}: {
  data: Feasibility;
  mode: FeasibilityMode;
  onChange: (mode: FeasibilityMode) => void;
  onFleet: () => void;
}) {
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={data.mode === 'basic' ? 'default' : 'outline'}
          onClick={() => onChange('basic')}
        >
          Базовый: сколько сцепок нужно
        </Button>
        <Button
          size="sm"
          variant={data.mode === 'full' ? 'default' : 'outline'}
          disabled={!data.full_mode_available}
          onClick={() => onChange('full')}
        >
          Полный: потребность против парка
          {!data.full_mode_available && ' · недоступен'}
        </Button>
        {mode !== 'auto' && (
          <Button size="sm" variant="ghost" onClick={() => onChange('auto')}>
            Вернуть автоматический выбор
          </Button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {data.full_mode_available
          ? `Парк хозяйства — ${data.fleet.machines} машин. Полный режим сверяет потребность с ним.`
          : 'Полный режим включится, когда в хозяйстве будет заведён парк техники.'}
        {data.fleet.machines_without_farm > 0 && (
          <>
            {' '}
            Ещё {data.fleet.machines_without_farm} машин компании не привязаны к хозяйству — в расчёт
            парка они не входят.{' '}
            <button type="button" className="underline" onClick={onFleet}>
              Открыть парк
            </button>
          </>
        )}
      </p>
    </div>
  );
}

/** Таймлайн агроокон: длина полосы — календарное окно, цифра — потребность. */
function Timeline({ windows }: { windows: FeasibilityWindow[] }) {
  const scale = useMemo(() => timelineScale(windows), [windows]);
  if (windows.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          Ни у одной операции назначенных техкарт не задано агроокно — считать потребность не из чего.
          Задайте агросроки в техкартах, и расчёт появится.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="text-sm font-semibold text-foreground">Агроокна сезона</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Длина полосы — календарное окно. Цифра справа — сколько сцепок нужно, чтобы успеть.
        </p>
        {/* Шапка месяцев повторяет СЕТКУ строк (210px имя · шкала · цифра),
            а не отступ «на глаз»: иначе подписи месяцев разъезжаются с
            полосами при первом же изменении ширины колонки. */}
        <div className="mt-3 grid grid-cols-[210px_1fr_120px] gap-3">
          <span />
          <div className="flex gap-1 text-[10px] uppercase text-muted-foreground">
            {scale.months.map((month, i) => (
              <span key={`${month}-${i}`} className="flex-1">
                {month}
              </span>
            ))}
          </div>
          <span />
        </div>
        <div className="mt-1 divide-y divide-border">
          {windows.map((w) => {
            const bar = timelineBar(w, scale);
            return (
              <div key={w.key} className="grid grid-cols-[210px_1fr_120px] items-center gap-3 py-2">
                <div className="text-xs">
                  <div className="font-semibold text-foreground">{w.label}</div>
                  <div className="text-muted-foreground">
                    {w.machinery_label} · {volumeText(w)}
                  </div>
                </div>
                <div className="relative h-4 rounded bg-muted">
                  <div
                    className={`absolute top-0.5 h-3 rounded ${barTone(w)}`}
                    style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                    title={formatWindow(w.starts_on, w.ends_on)}
                  />
                </div>
                <div className="text-right text-xs font-semibold tabular-nums">{needText(w)}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function barTone(w: FeasibilityWindow): string {
  if (w.severity === 'deficit') return 'bg-destructive';
  if (w.severity === 'overlap' || w.severity === 'tight' || w.severity === 'peak')
    return 'bg-accent-brand';
  return 'bg-primary/60';
}

function Bottlenecks({
  data,
  canWrite,
  onChangeCard,
  onChangePlan,
}: {
  data: Feasibility;
  canWrite: boolean;
  onChangeCard: () => void;
  onChangePlan: () => void;
}) {
  const [accepting, setAccepting] = useState<FeasibilityWindow | null>(null);
  const [comment, setComment] = useState('');
  const accept = useAcceptRisk();
  const cancel = useCancelRisk();
  const acceptedByKey = new Map(data.accepted_risks.map((r) => [r.bottleneck_key, r]));

  const heading = data.mode === 'full' ? 'Узкие места' : 'Что диктует размер парка';
  if (data.bottlenecks.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {data.mode === 'full'
            ? 'Узких мест нет: на каждое агроокно парка хватает.'
            : 'Потребность посчитана — окон, требующих отдельного решения, нет.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">
        {heading}{' '}
        <span className="font-normal text-muted-foreground">
          — по каждому решение принимает плановик
        </span>
      </h2>
      {data.bottlenecks.map((w) => {
        const risk = acceptedByKey.get(w.key);
        return (
          <Card key={w.key}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Окно {formatWindow(w.starts_on, w.ends_on)} · {w.label}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {volumeText(w)} за {w.work_days} рабочих дней · сцепка {w.machinery_label} ·{' '}
                    <b className="text-foreground">нужно {w.units_needed}</b>
                    {w.available !== null && `, есть ${w.available}`}
                    {w.severity === 'overlap' &&
                      ` — в эти же дни та же сцепка занята другой работой (одновременно нужно ${w.concurrent_units})`}
                    {w.severity === 'unknown_machinery' &&
                      ' — техника в техкарте не указана, сверить с парком нечем'}
                  </div>
                </div>
                <Badge variant={risk ? 'secondary' : w.deficit ? 'destructive' : 'outline'}>
                  {risk ? 'риск принят' : needText(w)}
                </Badge>
              </div>

              {risk && (
                <div className="mt-3 rounded border border-border p-2 text-xs">
                  «{risk.comment}»
                  <div className="mt-1 text-muted-foreground">
                    {risk.accepted_by ?? 'плановик'} ·{' '}
                    {new Date(risk.accepted_at).toLocaleString('ru-RU')} · решение видно в сравнении
                    вариантов
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {risk ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!canWrite || cancel.isPending}
                    onClick={() => cancel.mutate({ scenarioId: data.scenario.id, key: w.key })}
                  >
                    Отменить принятие
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canWrite}
                    onClick={() => {
                      setComment('');
                      setAccepting(w);
                    }}
                  >
                    Принять риск
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onChangeCard}>
                  Сменить техкарту
                </Button>
                <Button size="sm" variant="outline" onClick={onChangePlan}>
                  Сменить посевной план
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={accepting !== null} onOpenChange={(open) => !open && setAccepting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Принять риск</DialogTitle>
            <DialogDescription>
              {accepting &&
                `Окно ${formatWindow(accepting.starts_on, accepting.ends_on)} · ${accepting.label}: ${needText(accepting)}.`}{' '}
              Комментарий обязателен — через месяц «принято» без объяснения не отличить от
              «прокликали».
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="risk-comment">Почему идём так</Label>
            <Input
              id="risk-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Например: досеем за счёт двухсменной работы"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccepting(null)}>
              Отмена
            </Button>
            <Button
              disabled={comment.trim().length === 0 || accept.isPending}
              onClick={() => {
                if (!accepting) return;
                accept.mutate(
                  { scenarioId: data.scenario.id, key: accepting.key, comment: comment.trim() },
                  { onSuccess: () => setAccepting(null) },
                );
              }}
            >
              Принять риск
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerdictCard({ data }: { data: Feasibility }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">Вердикт</h3>
        <p className="mt-1 text-sm text-muted-foreground">{verdictTitle(data)}</p>
        <dl className="mt-3 space-y-1 text-sm">
          <Row label="Окон в расчёте" value={String(data.verdict.windows)} />
          <Row
            label={data.mode === 'full' ? 'Узких мест' : 'Окон, задающих парк'}
            value={String(data.verdict.bottlenecks)}
          />
          <Row label="Риски приняты" value={String(data.verdict.accepted)} />
          <Row label="Пиковая потребность" value={`${data.verdict.peak_units} сцепок`} />
        </dl>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-1 last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

/** FR-4.5 — что в расчёт НЕ попало, поимённо. */
function ExclusionsCard({ data }: { data: Feasibility }) {
  if (data.exclusions.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">Исключено из расчёта</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Эти операции в потребность не вошли — список показываем целиком, чтобы «выполнимо» не
          означало «посчитали не всё».
        </p>
        <ul className="mt-2 space-y-2 text-xs">
          {data.exclusions.map((e) => (
            <li key={`${e.operation_id}-${e.reason}`} className="border-b border-border pb-2 last:border-b-0">
              <span className="font-semibold text-foreground">{e.label}</span>
              <span className="block text-muted-foreground">
                {e.card_name} — {e.reason_text}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function FleetCard({ data }: { data: Feasibility }) {
  if (data.mode === 'full') {
    return (
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground">Парк хозяйства</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Считаем только технику этого хозяйства: холдинговая машина без хозяйства не выедет в поле
            вместо неё.
          </p>
          <ul className="mt-2 space-y-1 text-xs">
            {Object.entries(data.fleet.units_available).map(([label, count]) => (
              <li key={label} className="flex justify-between">
                <span className="text-muted-foreground">{label}</span>
                <span className="tabular-nums text-foreground">{count}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">Что даст полный режим</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Ту же таблицу окон, но с колонкой «есть в парке»: «нужно 6 — есть 4, дефицит 2». Для этого
          нужен заведённый парк техники хозяйства.
        </p>
      </CardContent>
    </Card>
  );
}
